import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../../hooks";
import { useCollectionGroups } from "../../../../hooks/useCollectionGroups";
import { useHistory } from "react-router";
import { Routes } from "../../../../root/routes";
import { PageLoading } from "../../../../components";
import { CollectionGroupDetailsDto } from "../../../../../common/service/server/dto/CollectionGroupDto";
import { useDataMap } from "../../../../../common/hooks/api/useDataMap";
import { useDataMapPendingCollectedPicIds } from "../../../../../common/service/resources/data-map/dataMapPending";
import { useDataMapCollectedPICIds } from "../../../../../common/service/resources/data-map/dataMapTransformers";
import { useSurvey } from "../../../../../common/hooks/api/useSurvey";
import {
  collectsCommercialInfoSlug,
  EMPLOYEE_COLLECTION_PURPOSES_SURVEY,
} from "../../../../components/organisms/collection-groups/EmploymentGroupPurposesQuestion";
import { MappingProgressEnum } from "../../../../../common/service/server/Types";
import { Api } from "../../../../../common/service/Api";
import { useActionRequest } from "../../../../../common/hooks/api";
import { EmployeeDataCollectionForm } from "../../../../components/organisms/collection-groups/EmployeeDataCollectionForm";
import { Error as OrganizationError } from "../../../../copy/organization";
import { ActionRow } from "../../../../components/ActionRow";
import { ProgressSpan } from "../../../get-compliant/ProgressSpan";
import { Typography } from "@mui/material";
import { EmployeePageWrapper } from "./EmployeePageWrapper";

export function EmployeeCollectionPage() {
  const [selectedCollectionGroupId, setSelectedCollectionGroupId] = useState<string | null>(null);

  if (selectedCollectionGroupId) {
    return (
      <CollectionGroupDetailsPage
        collectionGroupId={selectedCollectionGroupId}
        navigateToIndex={() => setSelectedCollectionGroupId(null)}
      />
    );
  } else {
    return (
      <CollectionIndexPage onCollectionRowClick={(cg) => setSelectedCollectionGroupId(cg.id)} />
    );
  }
}

function CollectionIndexPage({
  onCollectionRowClick,
}: {
  onCollectionRowClick: (cg: CollectionGroupDetailsDto) => void;
}) {
  const history = useHistory();

  const organizationId = usePrimaryOrganizationId();

  const [dataSubjectTypes, dstRequest] = useCollectionGroups(organizationId);

  const employeeGroups = useMemo(
    () => dataSubjectTypes.filter((dst) => dst.collectionGroupType == "EMPLOYMENT"),
    [dataSubjectTypes],
  );

  const anyNotDone = employeeGroups.some(
    (eg) =>
      eg.mappingProgress.find((p) => p.personalInformationType == "CATEGORY")?.value != "DONE",
  );

  if (dstRequest.running) {
    return <PageLoading />;
  }

  return (
    <EmployeePageWrapper
      selectedTabKey="employee-collection"
      nextOnClick={() => history.push(Routes.hrCenter.hrDataMap.employeeRecipients)}
      nextDisabled={anyNotDone}
      nextDisabledTooltip="Map your employment groups before proceeding."
    >
      <div className="tw-boundary" style={{ marginBottom: "60px" }}>
        <p>Map your business's collection practices for each Employment Group below.</p>

        <p className="mt-2">This section may require input from your HR team.</p>
      </div>

      <div style={{ maxWidth: "800px" }}>
        <CollectionBody
          employeeGroups={employeeGroups}
          onCollectionRowClick={(cg) => onCollectionRowClick(cg)}
        />
      </div>
    </EmployeePageWrapper>
  );
}

function CollectionGroupDetailsPage({
  collectionGroupId,
  navigateToIndex,
}: {
  collectionGroupId: string;
  navigateToIndex: () => void;
}) {
  const [org, orgRequest] = usePrimaryOrganization();
  const orgId = org.id;

  // Requests
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const [collectionGroups, consumerGroupsRequest] = useCollectionGroups(orgId);

  const group = useMemo(
    () => collectionGroups?.find((cg) => cg?.id === collectionGroupId),
    [collectionGroups, collectionGroupId],
  );

  const pending = useDataMapPendingCollectedPicIds(dataMap, {
    collectionGroupId: group?.id,
  });
  const collected = useDataMapCollectedPICIds(dataMap, { collectionGroupId: group?.id });

  const {
    answers,
    setAnswer,
    request: surveyRequest,
  } = useSurvey(orgId, group ? `consumer-data-collection-${group?.id}` : undefined);

  const { answers: collectionAnswers } = useSurvey(orgId, EMPLOYEE_COLLECTION_PURPOSES_SURVEY);
  const collectionSlug = collectsCommercialInfoSlug(group);

  const updateMappingProgressApi = useCallback(
    (progress: MappingProgressEnum) => {
      return Api.consumerGroups.updateMappingProgress(orgId, group?.id, "CATEGORY", progress);
    },
    [orgId, group?.id],
  );

  const updateMappingMessages = useMemo(
    () => ({
      forceError: OrganizationError.UpdateMappingProgress,
    }),
    [],
  );

  const { fetch: updateMappingProgress } = useActionRequest({
    api: updateMappingProgressApi,
    messages: updateMappingMessages,
  });

  // Effects
  useEffect(() => {
    const current = group?.mappingProgress?.find((p) => p.personalInformationType == "CATEGORY");
    if (current?.value == "NOT_STARTED") {
      updateMappingProgress("IN_PROGRESS");
    }
  }, [updateMappingProgress, group?.mappingProgress]);

  const nextDisabled = !collectionAnswers[collectionSlug] || collected.length === 0;

  if (
    !group ||
    orgRequest.running ||
    consumerGroupsRequest.running ||
    surveyRequest.running ||
    dataMapRequest.running
  ) {
    return <PageLoading />;
  }

  return (
    <EmployeePageWrapper
      selectedTabKey="employee-collection"
      alwaysShowFooter={true}
      nextContent="Back to Collections"
      nextOnClick={async () => {
        await updateMappingProgress("DONE");
        navigateToIndex();
      }}
      nextDisabled={nextDisabled}
      nextDisabledTooltip="Please make a selection."
    >
      <div style={{ maxWidth: "800px" }}>
        <h4 className="intermediate">What information do you collect about {group?.name}?</h4>

        <EmployeeDataCollectionForm
          pending={pending}
          collected={collected}
          dataMap={dataMap}
          org={org}
          collectionGroup={group}
          answers={answers}
          setAnswer={setAnswer}
        />
      </div>
    </EmployeePageWrapper>
  );
}

function CollectionBody({
  employeeGroups,
  onCollectionRowClick,
}: {
  employeeGroups: CollectionGroupDetailsDto[];
  onCollectionRowClick?: (cg: CollectionGroupDetailsDto) => void;
}) {
  if (employeeGroups.length == 0) {
    return (
      <div className="empty-state text-style-italic">
        <Typography variant="body1" paragraph>
          Based on your Survey responses, this step is not required. Click “Done” to proceed to the
          next step.
        </Typography>
      </div>
    );
  }
  return (
    <>
      <EmployeeGroupsList
        employeeGroups={employeeGroups}
        onCollectionRowClick={onCollectionRowClick}
      />
    </>
  );
}

function EmployeeGroupsList({
  employeeGroups,
  onCollectionRowClick,
}: {
  employeeGroups: CollectionGroupDetailsDto[];
  onCollectionRowClick?: (cg: CollectionGroupDetailsDto) => void;
}) {
  return (
    <>
      {employeeGroups
        .slice(0)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((cg, idx) => {
          const categoryProgress = cg.mappingProgress.find(
            (p) => p.personalInformationType == "CATEGORY",
          );
          return (
            <ActionRow
              key={`group-${cg.id}`}
              label={cg.name}
              topBorder={idx == 0}
              status={<ProgressSpan progress={categoryProgress?.value} />}
              onRowClick={() => onCollectionRowClick?.(cg)}
            />
          );
        })}
    </>
  );
}
